import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn.js'
import { Icon } from './Icon.js'
import { trapTab, useOverlay } from './overlay.js'

/**
 * A modal for short-lived overlays that should not replace the page underneath.
 *
 * Used for the job card on the dispatch board (so the board position is not
 * lost) and the user edit popup on Settings → Users (so the list stays put
 * while the full ServiceM8 staff record is reviewed).
 *
 * On a phone the job card presents as a `Sheet` instead. Escape handling, the
 * scroll lock and the stacking order are shared between the two through
 * `useOverlay`.
 *
 * The scrim is ink-900 at 40%, one of only two places transparency appears.
 */

export function Modal({
  open,
  onClose,
  labelledBy,
  className,
  children,
}: {
  open: boolean
  onClose: () => void
  labelledBy?: string
  className?: string
  children: ReactNode
}) {
  const panel = useRef<HTMLDivElement>(null)
  const depth = useOverlay(open, onClose)

  // Move focus in so Escape and Tab land somewhere sensible.
  useEffect(() => {
    if (open) panel.current?.focus()
  }, [open])

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 flex items-end justify-center bg-ink-900/40 sm:items-center sm:p-4"
      style={{ zIndex: 50 + depth * 10 }}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        onKeyDown={(event) => trapTab(event, event.currentTarget)}
        className={cn(
          // A phone gets the dialog as a sheet off the bottom edge, where the
          // thumb already is; a desk gets it centred.
          'flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-lg bg-white pb-[var(--safe-bottom)] shadow-xl outline-none sm:max-h-full sm:rounded-lg sm:pb-0',
          className,
        )}
      >
        {children}
      </div>
    </div>,
    document.body,
  )
}

export function ModalCloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label="Close"
      className="grid size-8 shrink-0 place-items-center rounded-md text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-900"
    >
      <Icon name="close" />
    </button>
  )
}

/**
 * The top of a dialog: what it is, a line on what it does, and the close
 * button. Every form dialog uses this so they read as one family.
 *
 * `aside` is a short fact beside the title — the document number, the job,
 * the client — and `lead` the person or the kind of thing it is about, drawn
 * before it. Neither is a second title.
 */
export function DialogHeader({
  id,
  title,
  description,
  aside,
  lead,
  onClose,
}: {
  id: string
  title: ReactNode
  description?: ReactNode
  aside?: ReactNode
  lead?: ReactNode
  onClose: () => void
}) {
  return (
    <div className="flex shrink-0 items-start gap-3 border-b border-ink-100 px-4 py-3">
      {lead ? <div className="flex min-h-[23px] shrink-0 items-center">{lead}</div> : null}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <h2 id={id} className="font-display text-[17px] leading-snug font-extrabold text-ink-900">
            {title}
          </h2>
          {aside ? <span className="min-w-0 truncate text-xs text-ink-500">{aside}</span> : null}
        </div>
        {description ? <p className="mt-0.5 text-xs text-ink-500">{description}</p> : null}
      </div>
      <ModalCloseButton onClose={onClose} />
    </div>
  )
}

/** The scrolling middle of a dialog. */
export function DialogBody({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn('flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-3', className)}>
      {children}
    </div>
  )
}

/**
 * The bottom of a dialog: an error or a hint on the left, the actions on the
 * right. It stays put while the body scrolls, so the save button is never
 * below the fold on a phone.
 *
 * `start` is an action that undoes rather than saves — Unbook, Remove —
 * kept on the left, away from the primary.
 */
export function DialogFooter({
  error,
  hint,
  start,
  children,
}: {
  error?: string | null
  hint?: ReactNode
  start?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-ink-100 bg-ink-50/60 px-4 py-2.5">
      {start ? <div className="mr-auto flex items-center gap-2">{start}</div> : null}
      {error ? (
        <p className={cn('text-xs text-red-600', start ? null : 'mr-auto')} role="alert">
          {error}
        </p>
      ) : hint ? (
        // Live, because a hint is usually what the form will do — "Next visit
        // 4 March", "2 services picked" — and it changes as it is filled in.
        <p className="min-w-0 flex-1 text-xs text-ink-500" aria-live="polite">
          {hint}
        </p>
      ) : null}
      {children}
    </div>
  )
}
